/// <amd-module name="Wiki/Clients/Wiki/RestClient/Wiki" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\client\wiki\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Comments_Contracts from "Comments/Common/Contracts/Comments";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";
import * as VC_Contracts from "Wiki/Clients/VC/Contracts/Git";
import * as Wiki from "Wiki/Clients/Wiki/Contracts/Wiki";

export interface IWikiRestClient extends IVssRestClient {
    /**
     * Creates an attachment in the wiki.
     * 
     * @param content - Content to upload
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param name - Wiki attachment name.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    createAttachment(content: any, project: string, wikiIdentifier: string, name: string, versionDescriptor?: VC_Contracts.GitVersionDescriptor): Promise<Wiki.WikiAttachmentResponse>;
    /**
     * Uploads an attachment on a comment on a wiki page.
     * 
     * @param content - Content to upload
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     */
    createCommentAttachment(content: any, project: string, wikiIdentifier: string, pageId: number): Promise<Comments_Contracts.CommentAttachment>;
    /**
     * Downloads an attachment on a comment on a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param attachmentId - Attachment ID.
     */
    getAttachmentContent(project: string, wikiIdentifier: string, pageId: number, attachmentId: string): Promise<ArrayBuffer>;
    /**
     * Add a reaction on a wiki page comment.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name
     * @param pageId - Wiki page ID
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction being added
     */
    addCommentReaction(project: string, wikiIdentifier: string, pageId: number, commentId: number, type: Comments_Contracts.CommentReactionType): Promise<Comments_Contracts.CommentReaction>;
    /**
     * Delete a reaction on a wiki page comment.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or name
     * @param pageId - Wiki page ID
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction being deleted
     */
    deleteCommentReaction(project: string, wikiIdentifier: string, pageId: number, commentId: number, type: Comments_Contracts.CommentReactionType): Promise<Comments_Contracts.CommentReaction>;
    /**
     * Gets a list of users who have reacted for the given wiki comment with a given reaction type. Supports paging, with a default page size of 100 users at a time.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction for which the engaged users are being requested
     * @param top - Number of enagaged users to be returned in a given page. Optional, defaults to 100
     * @param skip - Number of engaged users to be skipped to page the next set of engaged users, defaults to 0
     */
    getEngagedUsers(project: string, wikiIdentifier: string, pageId: number, commentId: number, type: Comments_Contracts.CommentReactionType, top?: number, skip?: number): Promise<VS_Services.IdentityRef[]>;
    /**
     * Add a comment on a wiki page.
     * 
     * @param request - Comment create request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     */
    addComment(request: Comments_Contracts.CommentCreateParameters, project: string, wikiIdentifier: string, pageId: number): Promise<Comments_Contracts.Comment>;
    /**
     * Delete a comment on a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or name.
     * @param pageId - Wiki page ID.
     * @param id - Comment ID.
     */
    deleteComment(project: string, wikiIdentifier: string, pageId: number, id: number): Promise<void>;
    /**
     * Returns a comment associated with the Wiki Page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param id - ID of the comment to return.
     * @param excludeDeleted - Specify if the deleted comment should be skipped.
     * @param expand - Specifies the additional data retrieval options for comments.
     */
    getComment(project: string, wikiIdentifier: string, pageId: number, id: number, excludeDeleted?: boolean, expand?: Comments_Contracts.CommentExpandOptions): Promise<Comments_Contracts.Comment>;
    /**
     * Returns a pageable list of comments.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param top - Max number of comments to return.
     * @param continuationToken - Used to query for the next page of comments.
     * @param excludeDeleted - Specify if the deleted comments should be skipped.
     * @param expand - Specifies the additional data retrieval options for comments.
     * @param order - Order in which the comments should be returned.
     * @param parentId - CommentId of the parent comment.
     */
    listComments(project: string, wikiIdentifier: string, pageId: number, top?: number, continuationToken?: string, excludeDeleted?: boolean, expand?: Comments_Contracts.CommentExpandOptions, order?: Comments_Contracts.CommentSortOrder, parentId?: number): Promise<Comments_Contracts.CommentList>;
    /**
     * Update a comment on a wiki page.
     * 
     * @param comment - Comment update request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param id - Comment ID.
     */
    updateComment(comment: Comments_Contracts.CommentUpdateParameters, project: string, wikiIdentifier: string, pageId: number, id: number): Promise<Comments_Contracts.Comment>;
    /**
     * Creates a page move operation that updates the path and order of the page as provided in the parameters.
     * 
     * @param pageMoveParameters - Page more operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param comment - Comment that is to be associated with this page move.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    createPageMove(pageMoveParameters: Wiki.WikiPageMoveParameters, project: string, wikiIdentifier: string, comment?: string, versionDescriptor?: VC_Contracts.GitVersionDescriptor): Promise<Wiki.WikiPageMoveResponse>;
    /**
     * Creates or edits a wiki page.
     * 
     * @param parameters - Wiki create or update operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param Version - Version of the page on which the change is to be made. Mandatory for \`Edit\` scenario. To be populated in the If-Match header of the request.
     * @param comment - Comment to be associated with the page operation.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    createOrUpdatePage(parameters: Wiki.WikiPageCreateOrUpdateParameters, project: string, wikiIdentifier: string, path: string, Version: String, comment?: string, versionDescriptor?: VC_Contracts.GitVersionDescriptor): Promise<Wiki.WikiPageResponse>;
    /**
     * Deletes a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param comment - Comment to be associated with this page delete.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    deletePage(project: string, wikiIdentifier: string, path: string, comment?: string, versionDescriptor?: VC_Contracts.GitVersionDescriptor): Promise<Wiki.WikiPageResponse>;
    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPage(project: string, wikiIdentifier: string, path?: string, recursionLevel?: VC_Contracts.VersionControlRecursionType, versionDescriptor?: VC_Contracts.GitVersionDescriptor, includeContent?: boolean): Promise<Wiki.WikiPageResponse>;
    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPageText(project: string, wikiIdentifier: string, path?: string, recursionLevel?: VC_Contracts.VersionControlRecursionType, versionDescriptor?: VC_Contracts.GitVersionDescriptor, includeContent?: boolean): Promise<string>;
    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPageZip(project: string, wikiIdentifier: string, path?: string, recursionLevel?: VC_Contracts.VersionControlRecursionType, versionDescriptor?: VC_Contracts.GitVersionDescriptor, includeContent?: boolean): Promise<ArrayBuffer>;
    /**
     * Deletes a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param id - Wiki page ID.
     * @param comment - Comment to be associated with this page delete.
     */
    deletePageById(project: string, wikiIdentifier: string, id: number, comment?: string): Promise<Wiki.WikiPageResponse>;
    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPageById(project: string, wikiIdentifier: string, id: number, recursionLevel?: VC_Contracts.VersionControlRecursionType, includeContent?: boolean): Promise<Wiki.WikiPageResponse>;
    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPageByIdText(project: string, wikiIdentifier: string, id: number, recursionLevel?: VC_Contracts.VersionControlRecursionType, includeContent?: boolean): Promise<string>;
    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    getPageByIdZip(project: string, wikiIdentifier: string, id: number, recursionLevel?: VC_Contracts.VersionControlRecursionType, includeContent?: boolean): Promise<ArrayBuffer>;
    /**
     * Edits a wiki page.
     * 
     * @param parameters - Wiki update operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param id - Wiki page ID.
     * @param Version - Version of the page on which the change is to be made. Mandatory for \`Edit\` scenario. To be populated in the If-Match header of the request.
     * @param comment - Comment to be associated with the page operation.
     */
    updatePageById(parameters: Wiki.WikiPageCreateOrUpdateParameters, project: string, wikiIdentifier: string, id: number, Version: String, comment?: string): Promise<Wiki.WikiPageResponse>;
    /**
     * Returns pageable list of Wiki Pages
     * 
     * @param pagesBatchRequest - Wiki batch page request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    getPagesBatch(pagesBatchRequest: Wiki.WikiPagesBatchRequest, project: string, wikiIdentifier: string, versionDescriptor?: VC_Contracts.GitVersionDescriptor): Promise<VS_Services.PagedList<Wiki.WikiPageDetail>>;
    /**
     * Returns page detail corresponding to Page ID.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param pageViewsForDays - last N days from the current day for which page views is to be returned. It's inclusive of current day.
     */
    getPageData(project: string, wikiIdentifier: string, pageId: number, pageViewsForDays?: number): Promise<Wiki.WikiPageDetail>;
    /**
     * Creates a new page view stats resource or updates an existing page view stats resource.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param wikiVersion - Wiki version.
     * @param path - Wiki page path.
     * @param oldPath - Old page path. This is optional and required to rename path in existing page view stats.
     */
    createOrUpdatePageViewStats(project: string, wikiIdentifier: string, wikiVersion: VC_Contracts.GitVersionDescriptor, path: string, oldPath?: string): Promise<Wiki.WikiPageViewStats>;
    /**
     * Creates the wiki resource.
     * 
     * @param wikiCreateParams - Parameters for the wiki creation.
     * @param project - Project ID or project name
     */
    createWiki(wikiCreateParams: Wiki.WikiCreateParametersV2, project?: string): Promise<Wiki.WikiV2>;
    /**
     * Deletes the wiki corresponding to the wiki ID or wiki name provided.
     * 
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    deleteWiki(wikiIdentifier: string, project?: string): Promise<Wiki.WikiV2>;
    /**
     * Gets all wikis in a project or collection.
     * 
     * @param project - Project ID or project name
     */
    getAllWikis(project?: string): Promise<Wiki.WikiV2[]>;
    /**
     * Gets the wiki corresponding to the wiki ID or wiki name provided.
     * 
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    getWiki(wikiIdentifier: string, project?: string): Promise<Wiki.WikiV2>;
    /**
     * Updates the wiki corresponding to the wiki ID or wiki name provided using the update parameters.
     * 
     * @param updateParameters - Update parameters.
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    updateWiki(updateParameters: Wiki.WikiUpdateParameters, wikiIdentifier: string, project?: string): Promise<Wiki.WikiV2>;
}

class WikiRestClient extends RestClientBase implements IWikiRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Creates an attachment in the wiki.
     * 
     * @param content - Content to upload
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param name - Wiki attachment name.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    public async createAttachment(
        content: any,
        project: string,
        wikiIdentifier: string,
        name: string,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor
        ): Promise<Wiki.WikiAttachmentResponse> {

        const queryValues: any = {
            name: name,
            versionDescriptor: versionDescriptor
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/attachments/{name}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            customHeaders: {
                "Content-Type": "application/octet-stream",
            },
            queryParams: queryValues,
            body: content,
            isRawData: true,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiAttachmentResponse = {
                attachment: <Wiki.WikiAttachment>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Uploads an attachment on a comment on a wiki page.
     * 
     * @param content - Content to upload
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     */
    public async createCommentAttachment(
        content: any,
        project: string,
        wikiIdentifier: string,
        pageId: number
        ): Promise<Comments_Contracts.CommentAttachment> {

        return this.beginRequest<Comments_Contracts.CommentAttachment>({
            apiVersion: "5.2-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/attachments/{attachmentId}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId
            },
            customHeaders: {
                "Content-Type": "application/octet-stream",
            },
            body: content,
            isRawData: true
        });
    }

    /**
     * Downloads an attachment on a comment on a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param attachmentId - Attachment ID.
     */
    public async getAttachmentContent(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        attachmentId: string
        ): Promise<ArrayBuffer> {

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "5.2-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/attachments/{attachmentId}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                attachmentId: attachmentId
            }
        });
    }

    /**
     * Add a reaction on a wiki page comment.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name
     * @param pageId - Wiki page ID
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction being added
     */
    public async addCommentReaction(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        commentId: number,
        type: Comments_Contracts.CommentReactionType
        ): Promise<Comments_Contracts.CommentReaction> {

        return this.beginRequest<Comments_Contracts.CommentReaction>({
            apiVersion: "5.2-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{commentId}/reactions/{type}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                commentId: commentId,
                type: type
            }
        });
    }

    /**
     * Delete a reaction on a wiki page comment.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or name
     * @param pageId - Wiki page ID
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction being deleted
     */
    public async deleteCommentReaction(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        commentId: number,
        type: Comments_Contracts.CommentReactionType
        ): Promise<Comments_Contracts.CommentReaction> {

        return this.beginRequest<Comments_Contracts.CommentReaction>({
            apiVersion: "5.2-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{commentId}/reactions/{type}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                commentId: commentId,
                type: type
            }
        });
    }

    /**
     * Gets a list of users who have reacted for the given wiki comment with a given reaction type. Supports paging, with a default page size of 100 users at a time.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param commentId - ID of the associated comment
     * @param type - Type of the reaction for which the engaged users are being requested
     * @param top - Number of enagaged users to be returned in a given page. Optional, defaults to 100
     * @param skip - Number of engaged users to be skipped to page the next set of engaged users, defaults to 0
     */
    public async getEngagedUsers(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        commentId: number,
        type: Comments_Contracts.CommentReactionType,
        top?: number,
        skip?: number
        ): Promise<VS_Services.IdentityRef[]> {

        const queryValues: any = {
            '$top': top,
            '$skip': skip
        };

        return this.beginRequest<VS_Services.IdentityRef[]>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{commentId}/reactions/{type}/users",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                commentId: commentId,
                type: type
            },
            queryParams: queryValues
        });
    }

    /**
     * Add a comment on a wiki page.
     * 
     * @param request - Comment create request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     */
    public async addComment(
        request: Comments_Contracts.CommentCreateParameters,
        project: string,
        wikiIdentifier: string,
        pageId: number
        ): Promise<Comments_Contracts.Comment> {

        return this.beginRequest<Comments_Contracts.Comment>({
            apiVersion: "5.2-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId
            },
            body: request
        });
    }

    /**
     * Delete a comment on a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or name.
     * @param pageId - Wiki page ID.
     * @param id - Comment ID.
     */
    public async deleteComment(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        id: number
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.2-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                id: id
            }
        });
    }

    /**
     * Returns a comment associated with the Wiki Page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param id - ID of the comment to return.
     * @param excludeDeleted - Specify if the deleted comment should be skipped.
     * @param expand - Specifies the additional data retrieval options for comments.
     */
    public async getComment(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        id: number,
        excludeDeleted?: boolean,
        expand?: Comments_Contracts.CommentExpandOptions
        ): Promise<Comments_Contracts.Comment> {

        const queryValues: any = {
            excludeDeleted: excludeDeleted,
            '$expand': expand
        };

        return this.beginRequest<Comments_Contracts.Comment>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                id: id
            },
            queryParams: queryValues
        });
    }

    /**
     * Returns a pageable list of comments.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param top - Max number of comments to return.
     * @param continuationToken - Used to query for the next page of comments.
     * @param excludeDeleted - Specify if the deleted comments should be skipped.
     * @param expand - Specifies the additional data retrieval options for comments.
     * @param order - Order in which the comments should be returned.
     * @param parentId - CommentId of the parent comment.
     */
    public async listComments(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        top?: number,
        continuationToken?: string,
        excludeDeleted?: boolean,
        expand?: Comments_Contracts.CommentExpandOptions,
        order?: Comments_Contracts.CommentSortOrder,
        parentId?: number
        ): Promise<Comments_Contracts.CommentList> {

        const queryValues: any = {
            '$top': top,
            continuationToken: continuationToken,
            excludeDeleted: excludeDeleted,
            '$expand': expand,
            order: order,
            parentId: parentId
        };

        return this.beginRequest<Comments_Contracts.CommentList>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId
            },
            queryParams: queryValues
        });
    }

    /**
     * Update a comment on a wiki page.
     * 
     * @param comment - Comment update request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param id - Comment ID.
     */
    public async updateComment(
        comment: Comments_Contracts.CommentUpdateParameters,
        project: string,
        wikiIdentifier: string,
        pageId: number,
        id: number
        ): Promise<Comments_Contracts.Comment> {

        return this.beginRequest<Comments_Contracts.Comment>({
            apiVersion: "5.2-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/comments/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId,
                id: id
            },
            body: comment
        });
    }

    /**
     * Creates a page move operation that updates the path and order of the page as provided in the parameters.
     * 
     * @param pageMoveParameters - Page more operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param comment - Comment that is to be associated with this page move.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    public async createPageMove(
        pageMoveParameters: Wiki.WikiPageMoveParameters,
        project: string,
        wikiIdentifier: string,
        comment?: string,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor
        ): Promise<Wiki.WikiPageMoveResponse> {

        const queryValues: any = {
            comment: comment,
            versionDescriptor: versionDescriptor
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pageMoves",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues,
            body: pageMoveParameters,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageMoveResponse = {
                pageMove: <Wiki.WikiPageMove>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Creates or edits a wiki page.
     * 
     * @param parameters - Wiki create or update operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param Version - Version of the page on which the change is to be made. Mandatory for \`Edit\` scenario. To be populated in the If-Match header of the request.
     * @param comment - Comment to be associated with the page operation.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    public async createOrUpdatePage(
        parameters: Wiki.WikiPageCreateOrUpdateParameters,
        project: string,
        wikiIdentifier: string,
        path: string,
        Version: String,
        comment?: string,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            path: path,
            comment: comment,
            versionDescriptor: versionDescriptor
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            customHeaders: {
                "If-Match": Version,
            },
            queryParams: queryValues,
            body: parameters,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Deletes a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param comment - Comment to be associated with this page delete.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    public async deletePage(
        project: string,
        wikiIdentifier: string,
        path: string,
        comment?: string,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            path: path,
            comment: comment,
            versionDescriptor: versionDescriptor
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPage(
        project: string,
        wikiIdentifier: string,
        path?: string,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor,
        includeContent?: boolean
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            path: path,
            recursionLevel: recursionLevel,
            versionDescriptor: versionDescriptor,
            includeContent: includeContent
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPageText(
        project: string,
        wikiIdentifier: string,
        path?: string,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor,
        includeContent?: boolean
        ): Promise<string> {

        const queryValues: any = {
            path: path,
            recursionLevel: recursionLevel,
            versionDescriptor: versionDescriptor,
            includeContent: includeContent
        };

        return this.beginRequest<string>({
            apiVersion: "5.2-preview.1",
            httpResponseType: "text/plain",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided path. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param path - Wiki page path.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param versionDescriptor - GitVersionDescriptor for the page. Defaults to the default branch (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPageZip(
        project: string,
        wikiIdentifier: string,
        path?: string,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor,
        includeContent?: boolean
        ): Promise<ArrayBuffer> {

        const queryValues: any = {
            path: path,
            recursionLevel: recursionLevel,
            versionDescriptor: versionDescriptor,
            includeContent: includeContent
        };

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "5.2-preview.1",
            httpResponseType: "application/zip",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues
        });
    }

    /**
     * Deletes a wiki page.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param id - Wiki page ID.
     * @param comment - Comment to be associated with this page delete.
     */
    public async deletePageById(
        project: string,
        wikiIdentifier: string,
        id: number,
        comment?: string
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            comment: comment
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                id: id
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPageById(
        project: string,
        wikiIdentifier: string,
        id: number,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        includeContent?: boolean
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            recursionLevel: recursionLevel,
            includeContent: includeContent
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                id: id
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPageByIdText(
        project: string,
        wikiIdentifier: string,
        id: number,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        includeContent?: boolean
        ): Promise<string> {

        const queryValues: any = {
            recursionLevel: recursionLevel,
            includeContent: includeContent
        };

        return this.beginRequest<string>({
            apiVersion: "5.2-preview.1",
            httpResponseType: "text/plain",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                id: id
            },
            queryParams: queryValues
        });
    }

    /**
     * Gets metadata or content of the wiki page for the provided page id. Content negotiation is done based on the \`Accept\` header sent in the request.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name..
     * @param id - Wiki page ID.
     * @param recursionLevel - Recursion level for subpages retrieval. Defaults to \`None\` (Optional).
     * @param includeContent - True to include the content of the page in the response for Json content type. Defaults to false (Optional)
     */
    public async getPageByIdZip(
        project: string,
        wikiIdentifier: string,
        id: number,
        recursionLevel?: VC_Contracts.VersionControlRecursionType,
        includeContent?: boolean
        ): Promise<ArrayBuffer> {

        const queryValues: any = {
            recursionLevel: recursionLevel,
            includeContent: includeContent
        };

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "5.2-preview.1",
            httpResponseType: "application/zip",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                id: id
            },
            queryParams: queryValues
        });
    }

    /**
     * Edits a wiki page.
     * 
     * @param parameters - Wiki update operation parameters.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param id - Wiki page ID.
     * @param Version - Version of the page on which the change is to be made. Mandatory for \`Edit\` scenario. To be populated in the If-Match header of the request.
     * @param comment - Comment to be associated with the page operation.
     */
    public async updatePageById(
        parameters: Wiki.WikiPageCreateOrUpdateParameters,
        project: string,
        wikiIdentifier: string,
        id: number,
        Version: String,
        comment?: string
        ): Promise<Wiki.WikiPageResponse> {

        const queryValues: any = {
            comment: comment
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{id}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                id: id
            },
            customHeaders: {
                "If-Match": Version,
            },
            queryParams: queryValues,
            body: parameters,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Wiki.WikiPageResponse = {
                page: <Wiki.WikiPage>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Returns pageable list of Wiki Pages
     * 
     * @param pagesBatchRequest - Wiki batch page request.
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param versionDescriptor - GitVersionDescriptor for the page. (Optional in case of ProjectWiki).
     */
    public async getPagesBatch(
        pagesBatchRequest: Wiki.WikiPagesBatchRequest,
        project: string,
        wikiIdentifier: string,
        versionDescriptor?: VC_Contracts.GitVersionDescriptor
        ): Promise<VS_Services.PagedList<Wiki.WikiPageDetail>> {

        const queryValues: any = {
            versionDescriptor: versionDescriptor
        };

        return this.beginRequest<Response>({
            apiVersion: "5.2-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pagesBatch",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues,
            body: pagesBatchRequest,
            returnRawResponse: true
        }).then(async response => {
            const body = <VS_Services.PagedList<Wiki.WikiPageDetail>>await response.text().then(deserializeVssJsonObject);
            body.continuationToken = response.headers.get("x-ms-continuationtoken");
            return body;
        });
    }

    /**
     * Returns page detail corresponding to Page ID.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param pageId - Wiki page ID.
     * @param pageViewsForDays - last N days from the current day for which page views is to be returned. It's inclusive of current day.
     */
    public async getPageData(
        project: string,
        wikiIdentifier: string,
        pageId: number,
        pageViewsForDays?: number
        ): Promise<Wiki.WikiPageDetail> {

        const queryValues: any = {
            pageViewsForDays: pageViewsForDays
        };

        return this.beginRequest<Wiki.WikiPageDetail>({
            apiVersion: "5.2-preview.1",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pages/{pageId}/stats",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier,
                pageId: pageId
            },
            queryParams: queryValues
        });
    }

    /**
     * Creates a new page view stats resource or updates an existing page view stats resource.
     * 
     * @param project - Project ID or project name
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param wikiVersion - Wiki version.
     * @param path - Wiki page path.
     * @param oldPath - Old page path. This is optional and required to rename path in existing page view stats.
     */
    public async createOrUpdatePageViewStats(
        project: string,
        wikiIdentifier: string,
        wikiVersion: VC_Contracts.GitVersionDescriptor,
        path: string,
        oldPath?: string
        ): Promise<Wiki.WikiPageViewStats> {

        const queryValues: any = {
            wikiVersion: wikiVersion,
            path: path,
            oldPath: oldPath
        };

        return this.beginRequest<Wiki.WikiPageViewStats>({
            apiVersion: "5.2-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}/pageViewStats/{*path}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            queryParams: queryValues
        });
    }

    /**
     * Creates the wiki resource.
     * 
     * @param wikiCreateParams - Parameters for the wiki creation.
     * @param project - Project ID or project name
     */
    public async createWiki(
        wikiCreateParams: Wiki.WikiCreateParametersV2,
        project?: string
        ): Promise<Wiki.WikiV2> {

        return this.beginRequest<Wiki.WikiV2>({
            apiVersion: "5.2-preview.2",
            method: "POST",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}",
            routeValues: {
                project: project
            },
            body: wikiCreateParams
        });
    }

    /**
     * Deletes the wiki corresponding to the wiki ID or wiki name provided.
     * 
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    public async deleteWiki(
        wikiIdentifier: string,
        project?: string
        ): Promise<Wiki.WikiV2> {

        return this.beginRequest<Wiki.WikiV2>({
            apiVersion: "5.2-preview.2",
            method: "DELETE",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            }
        });
    }

    /**
     * Gets all wikis in a project or collection.
     * 
     * @param project - Project ID or project name
     */
    public async getAllWikis(
        project?: string
        ): Promise<Wiki.WikiV2[]> {

        return this.beginRequest<Wiki.WikiV2[]>({
            apiVersion: "5.2-preview.2",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}",
            routeValues: {
                project: project
            }
        });
    }

    /**
     * Gets the wiki corresponding to the wiki ID or wiki name provided.
     * 
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    public async getWiki(
        wikiIdentifier: string,
        project?: string
        ): Promise<Wiki.WikiV2> {

        return this.beginRequest<Wiki.WikiV2>({
            apiVersion: "5.2-preview.2",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            }
        });
    }

    /**
     * Updates the wiki corresponding to the wiki ID or wiki name provided using the update parameters.
     * 
     * @param updateParameters - Update parameters.
     * @param wikiIdentifier - Wiki ID or wiki name.
     * @param project - Project ID or project name
     */
    public async updateWiki(
        updateParameters: Wiki.WikiUpdateParameters,
        wikiIdentifier: string,
        project?: string
        ): Promise<Wiki.WikiV2> {

        return this.beginRequest<Wiki.WikiV2>({
            apiVersion: "5.2-preview.2",
            method: "PATCH",
            routeTemplate: "{project}/_apis/wiki/wikis/{wikiIdentifier}",
            routeValues: {
                project: project,
                wikiIdentifier: wikiIdentifier
            },
            body: updateParameters
        });
    }

}

export const WikiClientName = "IWikiRestClient";

export function getWikiClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IWikiRestClient {
    return pageContext.getRestClient<IWikiRestClient>(WikiClientName, options);
}

RestClients.add(WikiClientName, { 
    factory: WikiRestClient,
    options: {
        resourceAreaId: "bf7d82a0-8aa5-4613-94ef-6172a5ea01f3",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
